<script setup lang="ts">
import Dialog from "primevue/dialog";
import { computed } from "vue";
import JSONtoHTML from "@/components/forms/SharedComponents/WYSIWYG/JSONtoHTML.vue";
import type { EventWithLocations } from "@/components/RegistrationProcess/types/events.ts";
import { getEventDuration } from "@/components/RegistrationProcess/utils/eventDuration";
import { useTenantTheming } from "@/composables/useTenantTheming.ts";
import { experienceDetailModalPresets } from "./styles";
import type { ExperienceDetailModalProps } from "./types";

/**
 * Detail modal for the program-page curated experiences section (AP-5593):
 * the legacy EventsModal look (accent header band with the event title,
 * description body, centered outlined CLOSE button) rebuilt on PrimeVue Dialog.
 */
defineOptions({
  name: "ExperienceDetailModal",
});

const visible = defineModel<boolean>("visible", { default: false });

// priceLabel is part of the public API but unused in the confirmed design.
const props = withDefaults(defineProps<ExperienceDetailModalProps>(), {
  priceLabel: "",
});

const { secondaryColor, secondaryColorClassOutlined } = useTenantTheming();

// Same header-band choice as the legacy EventsModal.
const dialogPt = computed(
  () => experienceDetailModalPresets(secondaryColor.value ? "bg-gray-275" : "bg-university-accentPrimary").dialog,
);

const event = computed<EventWithLocations>(() => props.excursion?.event ?? ({} as EventWithLocations));

const eventTitle = computed<string>(() => event.value?.title ?? "");

const duration = computed<string>(() => getEventDuration(event.value));

const description = computed<Record<string, unknown> | undefined>(() => event.value?.description ?? undefined);

const close = (): void => {
  visible.value = false;
};
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :showHeader="true"
    :closable="true"
    :modal="true"
    :dismissableMask="true"
    :closeOnEscape="true"
    :draggable="false"
    :resizable="false"
    :pt="dialogPt"
  >
    <template #header>
      <h3 class="uppercase font-semibold text-black" data-testid="experience-detail-modal-header">
        {{ eventTitle }}
      </h3>
    </template>
    <div class="text-indigo-base max-h-25vh overflow-y-auto pr-2" data-testid="experience-detail-modal">
      <p v-if="duration" class="mb-2 text-sm" data-testid="experience-detail-modal-duration">
        {{ duration }}
      </p>
      <JSONtoHTML
        v-if="description"
        class="text-justify w-full text-base text-indigo-base"
        :tiptap-json="description"
        data-testid="experience-detail-modal-description"
      />
    </div>
    <template #footer>
      <button
        type="button"
        class="border-2 font-bold py-2 px-4 uppercase"
        :class="secondaryColorClassOutlined"
        data-testid="experience-detail-modal-close"
        @click="close"
      >
        Close
      </button>
    </template>
  </Dialog>
</template>
